import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { fetchSsoProviders, ssoStartUrl, SsoIntent, SsoProvider } from 'src/api/functions/ssoApi';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { attributionParams, signupPage } from 'src/utils/campaign';
import messages from './messages';
import styles from './styles.module.scss';

// Providers this component knows how to draw. Anything else the backend
// might report is ignored: the buttons are a product surface, not a mirror
// of the configuration.
const KNOWN: Record<string, { label: string; icon: JSX.Element }> = {
  google: {
    label: 'Google',
    icon: (
      <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden="true">
        <path
          fill="#EA4335"
          d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
        />
        <path
          fill="#4285F4"
          d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
        />
        <path
          fill="#FBBC05"
          d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
        />
        <path
          fill="#34A853"
          d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
        />
      </svg>
    ),
  },
  microsoft: {
    label: 'Microsoft',
    icon: (
      <svg viewBox="0 0 23 23" width="20" height="20" aria-hidden="true">
        <rect x="1" y="1" width="10" height="10" fill="#F25022" />
        <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
        <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
        <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
      </svg>
    ),
  },
};

type Props = {
  // Same-origin path the user lands on after signing in. Anything else is
  // ignored server-side and replaced with /assets.
  returnTo?: string;
  // login (default): never creates an account. signup: may.
  intent?: SsoIntent;
  showDivider?: boolean;
  // Optional divider AFTER the buttons ("Or sign up with your work email").
  // Lives here so it disappears together with the buttons on a stack with
  // no providers.
  afterLabel?: React.ReactNode;
  // Called once with whether any provider is configured, so the parent can
  // adapt copy (e.g. the login failure hint) without a second request.
  onAvailability?: (available: boolean) => void;
  // signup only: the visitor ticked the ad-measurement opt-out
  // (src/utils/campaign.ts); sent as fs_ad_opt_out=1.
  adOptOut?: boolean;
  className?: string;
};

// One button per provider the backend has configured (Google, Microsoft).
// Renders nothing at all when the stack has none, so the page looks exactly
// as before on a stack without SSO.
export const SsoButtons = ({
  returnTo,
  intent = 'login',
  showDivider = true,
  afterLabel,
  onAvailability,
  adOptOut = false,
  className,
}: Props) => {
  const [providers, setProviders] = useState<SsoProvider[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchSsoProviders().then((r) => {
      if (cancelled || isErrorResponseResult(r)) return;
      const list = (Array.isArray(r.providers) ? r.providers : []).filter((p) => p in KNOWN);
      setProviders(list);
      onAvailability?.(list.length > 0);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (providers.length === 0) return null;

  // Full-page navigation on purpose: Google and Microsoft refuse to render
  // inside an iframe, and a popup would need its own postMessage protocol.
  const go = (provider: SsoProvider) => {
    const attribution =
      intent === 'signup'
        ? { utm: attributionParams(window.location.search, adOptOut), page: signupPage() }
        : undefined;
    window.location.assign(ssoStartUrl(provider, returnTo, intent, attribution));
  };

  return (
    <div className={[styles.wrapper, className].filter(Boolean).join(' ')} data-test="sso-buttons">
      {showDivider && (
        <div className={styles.divider}>
          <span />
          <strong>
            <FormattedMessage {...messages.orContinueWith} />
          </strong>
          <span />
        </div>
      )}
      <div className={styles.buttons}>
        {providers.map((p) => {
          const known = KNOWN[p];
          return (
            <button
              key={p}
              type="button"
              className={styles.button}
              onClick={() => go(p)}
              data-test={`sso-${p}`}
            >
              {known.icon}
              <span>
                <FormattedMessage
                  {...(intent === 'signup' ? messages.signUpWith : messages.continueWith)}
                  values={{ provider: known.label }}
                />
              </span>
            </button>
          );
        })}
      </div>
      <p className={styles.hint}>
        <FormattedMessage {...messages.workAccountHint} />
      </p>
      {afterLabel && (
        <div className={styles.divider}>
          <span />
          <strong>{afterLabel}</strong>
          <span />
        </div>
      )}
    </div>
  );
};

export default SsoButtons;
